import cn from '../../../utils/cn';
import Button from '../../atoms/Button/Button';
import breakpointStyles from '../../../styles/breakpoints.module.css';
import styles from './ResponsiveActionButton.module.css';

/**
 * Button that shows a full label on wide viewports and only the icon at or below the mobile breakpoint.
 * @param {object} props
 * @param {React.ReactNode} props.label - Label shown on wide viewports (wrapped with desktop-only visibility)
 * @param {string} props.ariaLabel - Accessible name (required: label is not exposed to AT on narrow viewports)
 * @param {keyof typeof import('../../atoms/Picto/Picto').availablePictos} props.icon
 * @param {'left'|'right'} [props.iconPosition='left']
 * @param {'iconAndLabel'|'labelOnly'} [props.desktopPresentation='iconAndLabel'] -
 *   `iconAndLabel`: icon + label on wide; `labelOnly`: label text on wide, icon hidden until narrow
 * @param {'primary'|'primary100'|'neutral00'|'alert'|'success'|'no-background'|'no-background-black'} [props.color='primary']
 * @param {'small'|'medium'} [props.size='medium']
 * @param {(event: React.MouseEvent<HTMLButtonElement>) => void} [props.onClick]
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} [props.isSubmit=false]
 * @param {string} [props.form]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
function ResponsiveActionButton({
  label,
  ariaLabel,
  icon,
  iconPosition = 'left',
  desktopPresentation = 'iconAndLabel',
  color = 'primary',
  size = 'medium',
  onClick,
  disabled = false,
  isLoading = false,
  isSubmit = false,
  form,
  className,
}) {
  return (
    <Button
      color={color}
      size={size}
      icon={icon}
      iconPosition={iconPosition}
      ariaLabel={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      isLoading={isLoading}
      isSubmit={isSubmit}
      form={form}
      className={cn([
        styles.root,
        desktopPresentation === 'labelOnly' && styles['label-led-desktop'],
        iconPosition === 'right' && styles['icon-end'],
        className,
      ])}
    >
      <span className={cn([breakpointStyles['desktop-only'], styles.label])}>{label}</span>
    </Button>
  );
}

export default ResponsiveActionButton;
